import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';

import { Text, Icon, Avatar, LetterAvatar } from '../../../../../components';

const DataDictionarySourceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
  transition: transform 200ms ease-in-out;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(-90deg)')};
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const ContentContainer = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 250ms ease-in-out, opacity 250ms ease-in-out;
  opacity: 0;

  &.entering,
  &.entered {
    max-height: 100%;
    opacity: 1;
  }

  &.exiting,
  &.exited {
    max-height: 0;
    opacity: 0;
  }
`;

const SourceContainer = styled.div.attrs({ role: 'button' })`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const DataDictionarySourceToggle = ({
  children,
  name,
  picture = null,
  isOpen,
  onToggle,
  handleSelectSchema
}) => (
  <DataDictionarySourceContainer>
    <ToggleContainer>
      <StyledIcon
        name='chevron-down'
        isOpen={isOpen}
        displaySize={20}
        onClick={onToggle}
        color='var(--content-subtitle)'
      />
      <SourceContainer onClick={handleSelectSchema}>
        {picture ? (
          <Avatar src={picture} width={22} height={22} />
        ) : (
          <LetterAvatar size={20} name={name} />
        )}
        <Text $color='var(--content-title)'>{name}</Text>
      </SourceContainer>
    </ToggleContainer>
    <Transition
      in={isOpen}
      timeout={250}
      unmountOnExit
      onEnter={(node) => {
        // Use requestAnimationFrame to avoid direct DOM manipulation
        requestAnimationFrame(() => {
          node.classList.add('entering');
        });
      }}
      onEntered={(node) => {
        // Ensure the element remains visible after transition
        node.classList.add('entered');
      }}
      onExit={(node) => {
        requestAnimationFrame(() => {
          node.classList.add('exiting');
        });
      }}
      onExited={(node) => {
        // Ensure it is hidden when transition completes
        node.classList.add('exited');
      }}
    >
      {(state) => <ContentContainer className={state}>{children}</ContentContainer>}
    </Transition>
  </DataDictionarySourceContainer>
);

DataDictionarySourceToggle.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  picture: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  handleSelectSchema: PropTypes.func.isRequired
};

export { DataDictionarySourceToggle };
